import { CreateCommentDto, EntityType } from '@nohara-dxdev/mt-common';
import { ApiResponse } from '@nohara-dxdev/oidc-core';
import { MutationConfig, useNotifications } from '@nohara-dxdev/react-core';
import { useScopeIds } from '@nohara-dxdev/react-oidc';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api-client';
import { CommentEntity } from '@/types/api';

import { getCommentsQueryKey } from './get-comments';
import { getConversationCommentsQueryKey } from './get-conversation-comments';

export const createComment = async (
  orgId: string,
  projectId: string,
  comment: CreateCommentDto,
): Promise<CommentEntity> => {
  const { data } = await api.post<unknown, ApiResponse<CommentEntity>>(
    `/orgs/${orgId}/projects/${projectId}/comments`,
    comment,
  );

  return data;
};

export interface UseCreateCommentOptions {
  path?: string;
  mutationConfig?: MutationConfig<(comment: string) => Promise<CommentEntity>>;
}

export const useCreateComment = (
  entityId: string,
  entityType: EntityType,
  { path = '', mutationConfig }: UseCreateCommentOptions = {},
) => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();
  const { orgId, projectId } = useScopeIds('org', 'project');

  const { onSuccess, onError, ...restConfig } = mutationConfig ?? {};

  return useMutation({
    mutationKey: ['comments', 'create', entityType, entityId, path],
    onSuccess: (...args) => {
      // Invalidate comments query for this entity
      void queryClient.invalidateQueries({
        queryKey: getCommentsQueryKey(orgId, projectId),
      });
      void queryClient.invalidateQueries({
        queryKey: getConversationCommentsQueryKey(orgId, projectId),
      });
      notifications.success('comments.create.success');
      onSuccess?.(...args);
    },
    onError: (...args) => {
      notifications.error('comments.create.error');
      onError?.(...args);
    },
    ...restConfig,
    mutationFn: (content: string) =>
      createComment(orgId, projectId, {
        content,
        path,
        entityType,
        entityId,
      }),
  });
};
